/*
  ProductInfo customizations for MORI product pages.
  - Keeps Dawn/Rise variant re-render behavior, but adds media ordering/filter sync for color variants.
  - Keeps duplicated gallery DOM aligned when section rendering leaves a hidden copy behind.
  - If product media starts breaking after a theme upgrade, review this file together with product-media-reinit.js.
*/
function waitForProductInfoRetry(delay, signal) {
  if (signal?.aborted) {
    return Promise.reject(new DOMException('Aborted', 'AbortError'));
  }

  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, delay);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true }
    );
  });
}

async function fetchHtmlOrThrow(url, options = {}, attempt = 0) {
  const response = await fetch(url, options);
  if (!response.ok) {
    const isTransient = response.status === 429 || response.status === 503;
    if (isTransient && attempt < 2 && !options.signal?.aborted) {
      const retryAfter = Number(response.headers?.get?.('retry-after')) * 1000;
      const fallbackDelay = attempt === 0 ? 500 : 1200;
      const delay = Number.isFinite(retryAfter) && retryAfter > 0 ? Math.min(retryAfter, 3000) : fallbackDelay;
      await waitForProductInfoRetry(delay, options.signal);
      return fetchHtmlOrThrow(url, options, attempt + 1);
    }

    throw new Error(`Failed to fetch product HTML: ${response.status} ${response.statusText}`);
  }
  const responseText = await response.text();
  return new DOMParser().parseFromString(responseText, 'text/html');
}

class ProductQuantityRulesController {
  constructor(host) {
    this.host = host;
  }

  init() {
    if (!this.host.quantityInput) return;

    this.host.quantityForm = this.host.querySelector('.product-form__quantity');
    if (!this.host.quantityForm) return;

    this.setBoundaries();
    if (!this.host.dataset.originalSection) {
      this.host.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchRules.bind(this));
    }
  }

  setBoundaries() {
    const quantityInput = this.host.quantityInput;
    if (!quantityInput) return;

    const data = {
      cartQuantity: quantityInput.dataset.cartQuantity ? parseInt(quantityInput.dataset.cartQuantity) : 0,
      min: quantityInput.dataset.min ? parseInt(quantityInput.dataset.min) : 1,
      max: quantityInput.dataset.max ? parseInt(quantityInput.dataset.max) : null,
      step: quantityInput.step ? parseInt(quantityInput.step) : 1,
    };

    let min = data.min;
    const max = data.max === null ? data.max : data.max - data.cartQuantity;
    if (max !== null) min = Math.min(min, max);
    if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

    quantityInput.min = min;

    if (max !== null) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }
    quantityInput.value = this.nextQuantityValue(quantityInput.value, min, max, data.step);

    publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
  }

  nextQuantityValue(currentValue, min, max, step) {
    const parsedValue = parseInt(currentValue, 10);
    if (!Number.isInteger(parsedValue)) return min;
    if (parsedValue < min) return min;
    if (max !== null && parsedValue > max) return this.nearestValidQuantity(max, min, max, step);
    if (!this.isValidStepValue(parsedValue, min, step)) return min;
    return parsedValue;
  }

  nearestValidQuantity(value, min, max, step) {
    if (!this.isValidStepValue(value, min, step)) {
      const normalizedStep = Number.isInteger(step) && step > 0 ? step : 1;
      const offset = Math.max(0, value - min);
      value = min + Math.floor(offset / normalizedStep) * normalizedStep;
    }
    if (max !== null && value > max) return max;
    return Math.max(value, min);
  }

  isValidStepValue(value, min, step) {
    const normalizedStep = Number.isInteger(step) && step > 0 ? step : 1;
    return (value - min) % normalizedStep === 0;
  }

  fetchRules() {
    const currentVariantId = this.host.productForm?.variantIdInput?.value;
    if (!currentVariantId) return;

    this.host.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
    return fetchHtmlOrThrow(`${this.host.dataset.url}?variant=${currentVariantId}&section_id=${this.host.dataset.section}`)
      .then((html) => {
        this.updateRules(this.host.dataset.section, html);
      })
      .catch((e) => console.error(e))
      .finally(() => this.host.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden'));
  }

  updateRules(sectionId, html) {
    if (!this.host.quantityInput || !this.host.quantityForm) return;

    const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
    if (!quantityFormUpdated) return;

    const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
    for (let selector of selectors) {
      const current = this.host.quantityForm.querySelector(selector);
      const updated = quantityFormUpdated.querySelector(selector);
      if (!current || !updated) continue;
      if (selector === '.quantity__input') {
        const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
        for (let attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute);
          if (valueUpdated !== null) {
            current.setAttribute(attribute, valueUpdated);
          } else {
            current.removeAttribute(attribute);
          }
        }
      } else {
        current.innerHTML = updated.innerHTML;
        if (selector === '.quantity__label') {
          const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
          if (updatedAriaLabelledBy) {
            current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
            const labelId = updatedAriaLabelledBy;
            const currentHiddenLabel = document.getElementById(labelId);
            const updatedHiddenLabel = html.getElementById(labelId);
            if (currentHiddenLabel && updatedHiddenLabel) {
              currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
            }
          }
        }
      }
    }

    this.setBoundaries();
  }
}

const ProductGalleryDomSyncController = window.RiseProductInfoMediaControllers?.ProductGalleryDomSyncController;
const ProductColorMediaFilterController = window.RiseProductInfoMediaControllers?.ProductColorMediaFilterController;

class ProductVariantRequestController {
  constructor(host) {
    this.host = host;
  }

  handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
    if (!this.host.contains(event.target)) return;
    if (!target) return;
    if (!Array.isArray(selectedOptionValues) || !selectedOptionValues.length) return;

    const productUrl = target.dataset.productUrl || this.host.pendingRequestUrl || this.host.dataset.url;
    this.host.scheduleImmediateColorFilter(target);
    this.host.applyImmediateVariantState(target);
    this.host.resetProductFormState();

    this.host.pendingRequestUrl = productUrl;
    const shouldSwapProduct = this.host.dataset.url !== productUrl;
    const shouldFetchFullPage = this.host.dataset.updateUrl === 'true' && shouldSwapProduct;
    const requestUrl = this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage);
    const now = Date.now();
    if (requestUrl === this.host.lastRequestSignature && now - this.host.lastRequestAt < 160) {
      if (!this.host.pendingRequestSignature) {
        this.host.variantSelectors?.resolvePendingSelectPromise(this.host.getSelectedVariant(this.host));
      }
      return;
    }
    this.host.lastRequestSignature = requestUrl;
    this.host.lastRequestAt = now;

    this.scheduleRenderProductInfo({
      requestUrl,
      targetId: target.id,
      callback: shouldSwapProduct
        ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
        : this.handleUpdateProductInfo(productUrl),
    });
  }

  scheduleRenderProductInfo(payload) {
    this.host.queuedRenderPayload = payload;
    if (this.host.requestRenderFrameId) return;
    this.host.requestRenderFrameId = requestAnimationFrame(() => {
      this.host.requestRenderFrameId = 0;
      const nextPayload = this.host.queuedRenderPayload;
      this.host.queuedRenderPayload = null;
      if (!nextPayload) return;
      this.renderProductInfo(nextPayload);
    });
  }

  renderProductInfo({ requestUrl, targetId, callback }) {
    if (this.host.pendingRequestSignature === requestUrl) return;
    this.host.abortController?.abort();
    this.host.abortController = new AbortController();
    this.host.pendingRequestSignature = requestUrl;
    const requestSignature = requestUrl;

    fetchHtmlOrThrow(requestUrl, { signal: this.host.abortController.signal })
      .then((html) => {
        callback(html);
      })
      .then(() => {
        document.querySelector(`#${targetId}`)?.focus();
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        } else {
          console.error(error);
        }
        this.host.variantSelectors?.rejectPendingSelectPromise(error);
      })
      .finally(() => {
        if (this.host.pendingRequestSignature === requestSignature) {
          this.host.pendingRequestSignature = null;
          this.host.pendingRequestUrl = null;
        }
      });
  }

  buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
    const requestUrl = new URL(url, window.location.origin);

    if (shouldFetchFullPage) {
      requestUrl.searchParams.delete('section_id');
    } else {
      requestUrl.searchParams.set('section_id', this.host.sectionId);
    }

    if (optionValues.length) {
      requestUrl.searchParams.set('option_values', optionValues.join(','));
    } else {
      requestUrl.searchParams.delete('option_values');
    }

    return `${requestUrl.pathname}${requestUrl.search}${requestUrl.hash}`;
  }

  handleSwapProduct(productUrl, updateFullPage) {
    return (html) => {
      this.host.productModal?.remove();

      const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
      const sourceProductInfo = html.querySelector(selector);
      const variant = this.host.getSelectedVariant(sourceProductInfo);
      this.host.variantSelectors?.resolvePendingSelectPromise(variant, sourceProductInfo?.querySelector('variant-selects'));
      this.host.updateURL(productUrl, variant?.id);

      if (updateFullPage) {
        document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

        HTMLUpdateUtility.viewTransition(
          document.querySelector('main'),
          html.querySelector('main'),
          this.host.preProcessHtmlCallbacks,
          this.host.postProcessHtmlCallbacks
        );
      } else {
        HTMLUpdateUtility.viewTransition(
          this.host,
          html.querySelector('product-info'),
          this.host.preProcessHtmlCallbacks,
          this.host.postProcessHtmlCallbacks
        );
      }
    };
  }

  handleUpdateProductInfo(productUrl) {
    return (html) => {
      const sourceVariantSelects = html.querySelector('variant-selects');
      const variant = this.host.getSelectedVariant(html);

      this.host.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);
      this.host.pickupAvailability?.update(variant);
      this.host.updateOptionValues(html);
      this.host.updateURL(productUrl, variant?.id);
      this.host.updateVariantInputs(variant?.id);

      if (!variant) {
        this.host.setUnavailable();
        return;
      }

      this.host.updateMedia(html, variant?.featured_media?.id);
      this.host.scheduleColorFilterSync(variant);

      const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
        const source = html.getElementById(`${id}-${this.host.sectionId}`);
        const destination = this.host.querySelector(`#${id}-${this.host.dataset.section}`);
        if (source && destination) {
          if (!this.host.optimizeVariantDomUpdates || destination.innerHTML !== source.innerHTML) {
            destination.innerHTML = source.innerHTML;
          }
          const shouldBeHidden = shouldHide(source);
          if (destination.classList.contains('hidden') !== shouldBeHidden) {
            destination.classList.toggle('hidden', shouldBeHidden);
          }
        }
      };

      updateSourceFromDestination('price');
      updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
      updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
      updateSourceFromDestination('Volume');
      updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

      this.host.updateQuantityRules(this.host.sectionId, html);
      this.host.querySelector(`#Quantity-Rules-${this.host.dataset.section}`)?.classList.remove('hidden');
      this.host.querySelector(`#Volume-Note-${this.host.dataset.section}`)?.classList.remove('hidden');

      this.host.productForm?.toggleSubmitButton(
        html.getElementById(`ProductSubmitButton-${this.host.sectionId}`)?.hasAttribute('disabled') ?? true,
        window.variantStrings.soldOut
      );

      this.publishVariantChangeAfterPaint({ html, variant });
    };
  }

  publishVariantChangeAfterPaint({ html, variant }) {
    if (this.host.variantChangePublishRafId) {
      cancelAnimationFrame(this.host.variantChangePublishRafId);
    }
    this.host.variantChangePublishRafId = requestAnimationFrame(() => {
      this.host.variantChangePublishRafId = 0;
      publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.host.sectionId,
          html,
          variant,
        },
      });
      this.dispatchVariantChangedEvent(html, variant);
    });
  }

  dispatchVariantChangedEvent(html, variant) {
    this.host.dispatchEvent(
      new CustomEvent('variant:changed', {
        bubbles: true,
        detail: {
          sectionId: this.host.sectionId,
          html,
          variant,
        },
      })
    );
  }
}

if (window.__RISE_PRODUCT_INFO_TEST__) {
  window.__RISE_PRODUCT_INFO_TEST__.helpers = {
    fetchHtmlOrThrow,
    ProductQuantityRulesController,
    ProductVariantRequestController,
  };
}

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      quantityRulesController = undefined;
      galleryDomSyncController = undefined;
      variantRequestController = undefined;
      colorMediaFilterController = undefined;
      mediaControllersReadyHandler = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      pendingRequestSignature = null;
      requestRenderFrameId = 0;
      queuedRenderPayload = null;
      lastRequestSignature = '';
      lastRequestAt = 0;
      lastAppliedColorFilterKey = '';
      lastAppliedColorFilterDomSignature = '';
      variantChangePublishRafId = 0;
      colorFilterSyncRafId = 0;
      colorFilterSyncTimeoutId = 0;
      immediateColorFilterRafId = 0;
      immediateColorFilterTimeoutId = 0;
      immediateColorFilterVariantId = '';
      pendingColorChangeScroll = false;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.quantityRulesController = new ProductQuantityRulesController(this);
        this.variantRequestController = new ProductVariantRequestController(this);
        this.initializeMediaControllers();
      }

      connectedCallback() {
        this.initializeMediaControllers();
        if (!this.galleryDomSyncController || !this.colorMediaFilterController) {
          this.mediaControllersReadyHandler = () => {
            this.initializeMediaControllers();
            this.filterGalleryBySelectedColor(this.getSelectedVariant(this));
          };
          window.addEventListener('rise:product-media-controllers-ready', this.mediaControllersReadyHandler, {
            once: true,
          });
        }

        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        const applyInitialColorSync = () => {
          this.filterGalleryBySelectedColor(this.getSelectedVariant(this));
        };
        applyInitialColorSync();
        requestAnimationFrame(applyInitialColorSync);
        customElements.whenDefined('media-gallery').then(() => {
          if (!this.isConnected) return;
          requestAnimationFrame(applyInitialColorSync);
        });
      }

      initializeMediaControllers() {
        const controllers = window.RiseProductInfoMediaControllers || {};
        const GalleryController = controllers.ProductGalleryDomSyncController || ProductGalleryDomSyncController;
        const ColorController = controllers.ProductColorMediaFilterController || ProductColorMediaFilterController;

        if (!this.galleryDomSyncController && GalleryController) {
          this.galleryDomSyncController = new GalleryController(this);
        }
        if (!this.colorMediaFilterController && ColorController) {
          this.colorMediaFilterController = new ColorController(this);
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        this.quantityRulesController?.init();
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        if (this.mediaControllersReadyHandler) {
          window.removeEventListener('rise:product-media-controllers-ready', this.mediaControllersReadyHandler);
          this.mediaControllersReadyHandler = undefined;
        }
        if (this.requestRenderFrameId) {
          cancelAnimationFrame(this.requestRenderFrameId);
          this.requestRenderFrameId = 0;
        }
        if (this.variantChangePublishRafId) {
          cancelAnimationFrame(this.variantChangePublishRafId);
          this.variantChangePublishRafId = 0;
        }
        if (this.colorFilterSyncRafId) {
          cancelAnimationFrame(this.colorFilterSyncRafId);
          this.colorFilterSyncRafId = 0;
        }
        if (this.colorFilterSyncTimeoutId) {
          clearTimeout(this.colorFilterSyncTimeoutId);
          this.colorFilterSyncTimeoutId = 0;
        }
        if (this.immediateColorFilterRafId) {
          cancelAnimationFrame(this.immediateColorFilterRafId);
          this.immediateColorFilterRafId = 0;
        }
        if (this.immediateColorFilterTimeoutId) {
          clearTimeout(this.immediateColorFilterTimeoutId);
          this.immediateColorFilterTimeoutId = 0;
        }
        this.immediateColorFilterVariantId = '';
        this.pendingColorChangeScroll = false;
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        this.variantRequestController?.handleOptionValueChange({ data: { event, target, selectedOptionValues } });
      }

      scheduleImmediateColorFilter(target) {
        if (!this.isColorSelectionTarget(target)) return;
        this.pendingColorChangeScroll = true;
        const variantIdFromTarget = String(target?.dataset?.optionVariantId || '');
        const variantIdFromSelectedOption = this.getSelectedOptionVariantId(target);
        this.immediateColorFilterVariantId = variantIdFromTarget || variantIdFromSelectedOption || '';
        if (this.immediateColorFilterRafId) {
          cancelAnimationFrame(this.immediateColorFilterRafId);
        }
        if (this.immediateColorFilterTimeoutId) {
          clearTimeout(this.immediateColorFilterTimeoutId);
          this.immediateColorFilterTimeoutId = 0;
        }
        this.immediateColorFilterRafId = requestAnimationFrame(() => {
          this.immediateColorFilterRafId = 0;
          if (!this.isConnected) return;
          this.immediateColorFilterTimeoutId = setTimeout(() => {
            this.immediateColorFilterTimeoutId = 0;
            if (!this.isConnected) return;
            this.colorMediaFilterController?.captureActiveThumbnailContext?.();
            const immediateVariant = this.getVariantForImmediateColorFilter(this.immediateColorFilterVariantId);
            this.immediateColorFilterVariantId = '';
            this.filterGalleryBySelectedColor(immediateVariant || this.getSelectedVariant(this), { defer: true });
          }, 120);
        });
      }

      isColorSelectionTarget(target) {
        const optionRoot = target?.closest?.('.product-form__input');
        if (!optionRoot) return false;
        const legendText = (optionRoot.querySelector('legend')?.textContent || '').replace(':', '').trim();
        const labelText = (optionRoot.querySelector('label.form__label')?.textContent || '')
          .replace(':', '')
          .trim();
        const combinedLabel = `${legendText} ${labelText}`.trim();
        return /(color|colour|\u30ab\u30e9\u30fc|\u8272)/i.test(combinedLabel);
      }

      getSelectedOptionVariantId(target) {
        const optionRoot = target?.closest?.('.product-form__input');
        if (!optionRoot) return '';
        const checkedInput = optionRoot.querySelector('input[type="radio"]:checked');
        if (checkedInput?.dataset?.optionVariantId) return String(checkedInput.dataset.optionVariantId);
        const selectedOption = optionRoot.querySelector('select option[selected]') || optionRoot.querySelector('select option:checked');
        if (selectedOption?.dataset?.optionVariantId) return String(selectedOption.dataset.optionVariantId);
        return '';
      }

      scheduleRenderProductInfo(payload) {
        this.variantRequestController?.scheduleRenderProductInfo(payload);
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      applyImmediateVariantState(target) {
        const variantId = String(target?.dataset?.optionVariantId || '');
        if (!variantId) return;

        this.updateVariantInputs(variantId, { dispatchChange: false, markPendingChange: true });

        const isAvailable = target?.dataset?.optionVariantAvailable;
        if (isAvailable === 'true') {
          this.productForm?.toggleSubmitButton(false);
          return;
        }

        if (isAvailable === 'false') {
          this.productForm?.toggleSubmitButton(true, window.variantStrings.soldOut);
        }
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return this.variantRequestController?.handleSwapProduct(productUrl, updateFullPage);
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.variantRequestController?.renderProductInfo({ requestUrl, targetId, callback });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode?.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        return this.variantRequestController?.buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage);
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          if (this.optimizeVariantDomUpdates && this.variantSelectors?.isEqualNode(variantSelects)) return;
          this.preProcessHtmlCallbacks?.forEach((callback) => callback(variantSelects));
          this.variantSelectors.replaceWith(variantSelects);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return this.variantRequestController?.handleUpdateProductInfo(productUrl);
      }

      publishVariantChangeAfterPaint({ html, variant }) {
        this.variantRequestController?.publishVariantChangeAfterPaint({ html, variant });
      }

      dispatchVariantChangedEvent(html, variant) {
        this.variantRequestController?.dispatchVariantChangedEvent(html, variant);
      }

      scheduleColorFilterSync(variant = null, options = {}) {
        this.colorMediaFilterController?.scheduleColorFilterSync(variant, options);
      }

      getColorOptionContext(variant = null) {
        return this.colorMediaFilterController?.getColorOptionContext(variant) || null;
      }

      isElementVisible(element) {
        return this.galleryDomSyncController?.isElementVisible(element) ?? false;
      }

      clearColorGalleryFilter(root = this) {
        (root || document)
          .querySelectorAll(
            '[data-color-filter-hidden], [data-color-filter-match], [data-color-filter-source], .color-filter-hidden'
          )
          .forEach((node) => {
            node.removeAttribute('data-color-filter-hidden');
            node.removeAttribute('data-color-filter-match');
            node.removeAttribute('data-color-filter-source');
            node.classList.remove('color-filter-hidden');
            if (node.style.display === 'none') {
              node.style.display = '';
            }
          });
      }

      getCurrentProductIdentityTerms() {
        const normalize = (value) =>
          String(value || '')
            .trim()
            .toLowerCase()
            .replace(/[\/／]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

        const terms = new Set();
        const title = this.querySelector('h1')?.textContent || '';
        const productUrl = this.dataset.url || '';
        const handle = productUrl.split('/products/')[1] || '';
        const genericTerms = new Set([
          'karhu',
          'hush puppies',
          'hush',
          'puppies',
          'aristo primo',
          'aristo',
          'primo',
          'blanc bleu',
          'blanc',
          'bleu',
          'men',
          'mens',
          'women',
          'womens',
          'ladies',
          'unisex',
          'メンズ',
          'レディース',
          'ユニセックス',
          'スニーカー',
          'パンプス',
          'ブーツ',
          'サンダル',
          'ローファー',
          'ビジネスシューズ',
          'カジュアルシューズ',
        ]);

        const pushIfUseful = (value) => {
          const normalized = normalize(value);
          if (!normalized || normalized.length < 4 || genericTerms.has(normalized)) return;
          terms.add(normalized);
        };

        pushIfUseful(handle);
        handle
          .split(/[-_]+/)
          .filter(Boolean)
          .forEach(pushIfUseful);

        const modelLikeTokens = title.match(/[A-Za-z]+(?:-[A-Za-z0-9]+)+|[A-Za-z]{4,}(?:\s+\d+)?/g) || [];
        modelLikeTokens.forEach(pushIfUseful);

        return Array.from(terms).sort((a, b) => b.length - a.length);
      }

      getVariantMediaGroupingData() {
        return this.colorMediaFilterController?.getVariantMediaGroupingData() || null;
      }

      getVariantForImmediateColorFilter(variantId) {
        const normalizedVariantId = String(variantId || '');
        if (!normalizedVariantId) return null;
        const data = this.getVariantMediaGroupingData();
        const record = Array.isArray(data?.variantRecords)
          ? data.variantRecords.find((item) => String(item?.id || '') === normalizedVariantId)
          : null;
        if (!record?.featuredMediaId) {
          return { id: normalizedVariantId };
        }
        return {
          id: normalizedVariantId,
          featured_media: {
            id: String(record.featuredMediaId),
          },
        };
      }

      getRawMediaId(value) {
        return this.galleryDomSyncController?.getRawMediaId(value) || '';
      }

      getOrderedVariantMediaContext(variant = null) {
        return this.colorMediaFilterController?.getOrderedVariantMediaContext(variant) || null;
      }

      buildColorMediaLookup(variantMediaGroupingData = null, colorValues = [], normalize = null) {
        return this.colorMediaFilterController?.buildColorMediaLookup(variantMediaGroupingData, colorValues, normalize) || null;
      }

      getCachedColorMediaLookup(variantMediaGroupingData = null, colorValues = [], normalize = null) {
        return this.colorMediaFilterController?.getCachedColorMediaLookup(variantMediaGroupingData, colorValues, normalize) || null;
      }

      getColorFilterDomSignature() {
        return this.colorMediaFilterController?.getColorFilterDomSignature() || '';
      }

      reorderColorFilteredItems(groups) {
        this.colorMediaFilterController?.reorderColorFilteredItems(groups);
      }

      refreshGalleryAfterColorFilter() {
        this.colorMediaFilterController?.refreshGalleryAfterColorFilter();
      }

      syncActiveMediaToColorSelection(variant, groups) {
        this.colorMediaFilterController?.syncActiveMediaToColorSelection(variant, groups);
      }

      scrollToGalleryAfterColorChange() {
        this.colorMediaFilterController?.scrollToGalleryAfterColorChange();
      }

      getVisibleMatch(root, selector) {
        return this.galleryDomSyncController?.getVisibleMatch(root, selector) || null;
      }

      getPreferredGalleryList(root = this) {
        return this.galleryDomSyncController?.getPreferredGalleryList(root) || null;
      }

      getPreferredThumbnailList(root = this) {
        return this.galleryDomSyncController?.getPreferredThumbnailList(root) || null;
      }

      syncDuplicateMediaListMarkup() {
        this.galleryDomSyncController?.syncDuplicateMediaListMarkup();
      }

      filterGalleryBySelectedColor(variant = null, options = {}) {
        this.colorMediaFilterController?.filterGalleryBySelectedColor(variant, options);
      }

      updateVariantInputs(variantId, options = {}) {
        const dispatchChange = options.dispatchChange !== false;
        const markPendingChange = options.markPendingChange === true;
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          const nextVariantId = variantId ? String(variantId) : '';
          const changed = input.value !== nextVariantId;
          input.value = nextVariantId;
          if (markPendingChange && changed) {
            input.dataset.risePendingVariantChange = nextVariantId;
          }
          if (!dispatchChange) return;
          const pendingVariantChange = input.dataset.risePendingVariantChange === nextVariantId;
          if (!changed && !pendingVariantChange) return;
          delete input.dataset.risePendingVariantChange;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const shareButton = this.querySelector('share-button');
        if (typeof shareButton?.updateUrl === 'function') {
          shareButton.updateUrl(`${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`);
        }

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        this.galleryDomSyncController?.updateMedia(html, variantFeaturedMediaId);
      }

      setQuantityBoundries() {
        this.quantityRulesController?.setBoundaries();
      }

      fetchQuantityRules() {
        return this.quantityRulesController?.fetchRules();
      }

      updateQuantityRules(sectionId, html) {
        this.quantityRulesController?.updateRules(sectionId, html);
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get optimizeVariantDomUpdates() {
        return this.dataset.optimizeVariantDom !== 'false';
      }
    }
  );
}
